import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import SplashScreen from './components/SplashScreen';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import Dashboard from './pages/Dashboard';
import FamilyCircle from './pages/FamilyCircle';
import FamilyTree from './pages/FamilyTree';
import StoryDetail from './pages/StoryDetail';
import Settings from './pages/Settings';
import Pricing from './pages/Pricing';
import Help from './pages/Help';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import EULA from './pages/EULA';
import AcceptInvite from './pages/AcceptInvite';
import { usePushNotifications } from './hooks/usePushNotifications';
import GiftPurchase from './pages/GiftPurchase';
import GiftSuccess from './pages/GiftSuccess';
import GiftRedeem from './pages/GiftRedeem';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner-large"></div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner-large"></div>
      </div>
    );
  }

  return isAuthenticated ? <Navigate to="/" /> : children;
};

// Redirect to backend-served wall page
const WallRedirect = () => {
  useEffect(() => {
    const url = window.location.pathname + window.location.search;
    window.location.replace(`https://echomemoir.ai${url}`);
  }, []);
  return null;
};

function AppRoutes() {
  usePushNotifications();
  
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route path="/gift" element={<GiftPurchase />} />
      <Route path="/gift/success" element={<GiftSuccess />} />
      <Route path="/redeem" element={<GiftRedeem />} />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/reset-password/:token"
        element={
          <PublicRoute>
            <ResetPassword />
          </PublicRoute>
        }
      />
      <Route
        path="/confirm-email/:token"
        element={<ConfirmEmail />}
      />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/eula" element={<EULA />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:id"
        element={
          <ProtectedRoute>
            <FamilyCircle />
          </ProtectedRoute>
        }
      />
      <Route
        path="/family/:id/tree"
        element={
          <ProtectedRoute>
            <FamilyTree />
          </ProtectedRoute>
        }
      />
      <Route
        path="/story/:id"
        element={
          <ProtectedRoute>
            <StoryDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <Pricing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <Help />
          </ProtectedRoute>
        }
      />
      <Route path="/wall/:circleId/:tokenId" element={<WallRedirect />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show animated splash once per session
    if (sessionStorage.getItem('echomemoir_splash_shown')) {
      return false;
    }
    return true;
  });

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem('echomemoir_splash_shown', 'true');
    setShowSplash(false);
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;